import { createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { cartLineId, getUnitPrice, lineLabel } from '../utils/variants';

// Load initial state from localStorage
const loadCartFromStorage = () => {
    try {
        const stored = localStorage.getItem('cart');
        const parsed = stored ? JSON.parse(stored) : { items: [] };
        const items = Array.isArray(parsed.items) ? parsed.items : [];
        // Carts saved before product options existed have no lineId
        return {
            items: items.map((item) => ({
                ...item,
                lineId: item.lineId || cartLineId(item.id, item.variantName),
            })),
        };
    } catch (e) {
        return { items: [] };
    }
};

const initialState = loadCartFromStorage();

const persist = (state) => {
    localStorage.setItem('cart', JSON.stringify(state));
};

const cartSlice = createSlice({
    name: 'cart',
    initialState,
    reducers: {
        // Accepts either a bare product, or { product, quantity, variant } where
        // variant is one of the product's named options.
        addToCart: (state, action) => {
            const payload = action.payload || {};
            const detailed = Boolean(payload.product);
            const product = detailed ? payload.product : payload;
            const quantity = detailed && payload.quantity ? payload.quantity : 1;
            const variant = detailed ? payload.variant : null;

            const variantName = variant && variant.name ? variant.name : null;
            const lineId = cartLineId(product.id, variantName);
            const label = lineLabel(product.title, variantName);

            const existingItem = state.items.find((item) => item.lineId === lineId);

            if (existingItem) {
                existingItem.quantity += quantity;
                toast.success(`Updated ${label} quantity (+${quantity})`, {
                    position: 'top-right',
                    autoClose: 2000,
                });
            } else {
                // The full options list is not worth carrying in localStorage;
                // the line already holds the resolved price.
                const { variants, ...productFields } = product;
                state.items.push({
                    ...productFields,
                    quantity,
                    lineId,
                    variantName,
                    selectedOptions: variantName,
                    // Cart is PKR-only, and getUnitPrice returns {amount, currency}
                    price: getUnitPrice(product, variant).amount || 0,
                });
                toast.success(`${label} added to cart (${quantity} ${quantity > 1 ? 'items' : 'item'})`, {
                    position: 'top-right',
                    autoClose: 2000,
                });
            }
            persist(state);
        },

        removeFromCart: (state, action) => {
            const lineId = action.payload;
            const removedItem = state.items.find((item) => item.lineId === lineId);
            if (removedItem) {
                state.items = state.items.filter((item) => item.lineId !== lineId);
                toast.info(`${lineLabel(removedItem.title, removedItem.variantName)} removed from cart`, {
                    position: 'top-right',
                    autoClose: 2000,
                });
                persist(state);
            }
        },

        updateQuantity: (state, action) => {
            const { lineId, quantity } = action.payload;
            const item = state.items.find((entry) => entry.lineId === lineId);
            if (item) {
                if (quantity <= 0) {
                    state.items = state.items.filter((entry) => entry.lineId !== lineId);
                    toast.info(`${lineLabel(item.title, item.variantName)} removed from cart`, {
                        position: 'top-right',
                        autoClose: 2000,
                    });
                } else {
                    item.quantity = quantity;
                }
                persist(state);
            }
        },

        clearCart: (state) => {
            state.items = [];
            localStorage.removeItem('cart');
            toast.info('Cart cleared', {
                position: 'top-right',
                autoClose: 2000,
            });
        },
    },
});

// Selectors
export const selectCartItems = (state) => state.cart.items;
export const selectCartTotal = (state) =>
    state.cart.items.reduce((total, item) => total + item.price * item.quantity, 0);

// ✅ Updated: count = number of distinct products (not total quantity)
export const selectCartCount = (state) => state.cart.items.length;

// Actions
export const { addToCart, removeFromCart, updateQuantity, clearCart } = cartSlice.actions;

export default cartSlice.reducer;
